import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import ModeToggle from '../components/ModeToggle';

export default function AuthPage() {
  const { mode, login, signup } = useApp();
  const nav = useNavigate();
  const isCyber = mode === 'cyber';
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      if (tab === 'login') await login(email, password);
      else await signup(email, password, name);
      nav('/');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Authentication failed');
    } finally { setLoading(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const inpCls = isCyber
    ? 'w-full px-4 py-3 bg-black border border-[#00FFD1]/40 rounded-lg font-mono-cyber text-[#00FFD1] placeholder-[#00FFD1]/30 focus:border-[#00FFD1] outline-none'
    : 'w-full px-4 py-3 bg-white border-2 border-[#C9B99A] rounded-lg font-body-story text-[#2C2621] focus:border-[#8C1C13] outline-none';

  return (
    <div className={`min-h-screen flex items-center justify-center px-6 ${isCyber ? 'bg-black scanlines' : ''}`}>
      <div className="absolute top-6 right-6"><ModeToggle /></div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className={`w-full max-w-md ${isCyber ? 'glass-cyber' : 'parchment'} rounded-2xl p-8`}>
        <div className={isCyber ? 'font-mono-cyber text-[#00FFD1] text-xs mb-1' : 'font-body-story italic text-[#68615A] mb-1'}>
          {isCyber ? '// authenticate' : 'Enter the realm'}
        </div>
        <h1 className={isCyber ? 'font-orbitron text-3xl mb-6' : 'font-storybook text-3xl mb-6'}>
          {tab === 'login' ? (isCyber ? 'SIGN IN' : 'Welcome Back') : (isCyber ? 'CREATE ACCOUNT' : 'Join the Realm')}
        </h1>

        <div className="flex gap-2 mb-6">
          {['login', 'signup'].map(t => (
            <button key={t} data-testid={`auth-tab-${t}`} onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-lg text-sm ${
                tab === t
                  ? (isCyber ? 'bg-[#00FFD1] text-black font-orbitron' : 'bg-[#2B5329] text-white font-storybook')
                  : (isCyber ? 'border border-neutral-800 text-neutral-400 font-mono-cyber' : 'border-2 border-[#C9B99A] font-body-story')
              }`}>
              {t === 'login' ? 'Sign In' : 'Sign Up'}
            </button>
          ))}
        </div>

        <form onSubmit={handle} className="space-y-4">
          {tab === 'signup' && (
            <input data-testid="auth-name" type="text" placeholder="Name" required value={name} onChange={e => setName(e.target.value)} className={inpCls} />
          )}
          <input data-testid="auth-email" type="email" placeholder="Email" required value={email} onChange={e => setEmail(e.target.value)} className={inpCls} />
          <input data-testid="auth-password" type="password" placeholder="Password (min 8)" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} className={inpCls} />
          {err && <div className="text-red-500 text-sm">{err}</div>}
          <button data-testid="auth-submit" disabled={loading} type="submit"
            className={`w-full py-3 rounded-lg font-bold ${isCyber ? 'bg-[#00FFD1] text-black font-orbitron tracking-widest' : 'storybook-btn'}`}>
            {loading ? '…' : (tab === 'login' ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className={`my-5 text-center text-xs ${isCyber ? 'text-neutral-500 font-mono-cyber' : 'text-[#68615A] font-body-story italic'}`}>— or —</div>

        <button data-testid="auth-google" onClick={googleSignIn}
          className={`w-full py-3 rounded-lg font-medium ${isCyber ? 'border border-[#00FFD1]/40 text-[#00FFD1] font-orbitron hover:bg-[#00FFD1]/10' : 'bg-white border-2 border-[#C9B99A] text-[#2C2621] font-body-story'}`}>
          Continue with Google
        </button>

        <button data-testid="auth-back-home" onClick={() => nav('/')} className={`mt-6 w-full text-sm ${isCyber ? 'text-neutral-500 font-mono-cyber' : 'text-[#68615A] font-body-story'}`}>
          ← back to home
        </button>
      </motion.div>
    </div>
  );
}
